import { execa, type ResultPromise } from 'execa'
import { join } from 'path'
import { access } from 'fs/promises'
import { getBpCli } from './bp-cli.js'
import { BaseCommand } from './base-command.js'
import { optimizeSourceMap } from '../utils/source-map-optimizer.js'

export interface BpDevCommandOptions {
  port: string
  botPath: string
  agentPath: string
  workspaceId: string
  credentials: {
    token: string
    apiUrl: string
  }
  sourceMap?: boolean
}

export type BpDevProgressEvent =
  | {
      type: 'generating'
      startTime: number
      endTime?: number
    }
  | {
      type: 'bundling'
      startTime: number
      endTime?: number
      data?: {
        buildTime: number
      }
    }
  | {
      type: 'deploying'
      startTime: number
      endTime?: number
      data?: {
        botId: string
        tunnelUrl: string
      }
    }
  | {
      type: 'listening'
      startTime: number
      data: {
        port: number
      }
    }

export class BpDevCommand extends BaseCommand<BpDevProgressEvent> {
  private childProcess: ResultPromise | null = null
  private killed = false
  private stderrLines: string[] = []
  private readonly MAX_STDERR_LINES = 50

  // Track progress events
  private progressEvents: Map<BpDevProgressEvent['type'], BpDevProgressEvent> = new Map()

  constructor(private options: BpDevCommandOptions) {
    super()
  }

  async run(): Promise<void> {
    const { port, botPath, workspaceId, credentials, sourceMap = true } = this.options

    // Get bp command path
    const { path: bpCommand } = await getBpCli(botPath)

    const bpArgs = [
      'dev',
      '--port',
      port,
      '--token',
      credentials.token,
      '--workspaceId',
      workspaceId,
      '--apiUrl',
      credentials.apiUrl,
    ]

    if (sourceMap) {
      bpArgs.push('--sourceMap')
    }

    this.childProcess = execa(bpCommand, bpArgs, {
      cwd: botPath,
      env: {
        ...process.env,
        TRACE_DIR: join(botPath, 'traces'),
        NODE_ENV: 'development',

        // force using "node" for LLMz engine
        // remove this when LLMz supports wasm (quickjs)
        VM_DRIVER: 'node',

        // in dev mode, we provide the user's PAT so the agent can access
        // restricted resources like adding new integrations
        ADK_LOCAL_PAT: credentials.token,

        WORKER_MODE: 'true',
        WORKER_LIFETIME_MS: process.env.WORKER_LIFETIME_MS || '120000', // Default 2 minutes

        ADK_DIRECTORY: join(botPath, '..'),
        AGENT_DIRECTORY: this.options.agentPath,
        // Enable source maps for better debugging support
        NODE_OPTIONS: `${process.env.NODE_OPTIONS || ''} --enable-source-maps`.trim(),
      },
      stdio: ['ignore', 'pipe', 'pipe'],
    })

    // Capture stdout
    if (this.childProcess.stdout) {
      this.childProcess.stdout.on('data', (data: Buffer) => {
        const text = data.toString()
        this.parseOutput(text)
        this.emit('stdout', text)
      })
    }

    // Capture stderr
    if (this.childProcess.stderr) {
      this.childProcess.stderr.on('data', (data: Buffer) => {
        const text = data.toString()

        // Track stderr lines separately (keep last N lines)
        const newLines = text.split('\n').filter((line) => line.trim())
        this.stderrLines.push(...newLines)
        if (this.stderrLines.length > this.MAX_STDERR_LINES) {
          this.stderrLines = this.stderrLines.slice(-this.MAX_STDERR_LINES)
        }

        // Check for fatal errors in stderr
        const lowerText = text.toLowerCase()
        let fatalError: string | null = null

        if (lowerText.includes('eaddrinuse') || lowerText.includes('address already in use')) {
          fatalError = `Port ${port} is already in use. Please stop the other process or use a different port.`
        } else if (lowerText.includes('eacces') || lowerText.includes('permission denied')) {
          fatalError = 'Permission denied. You may need administrator privileges.'
        } else if (lowerText.includes('econnrefused') || lowerText.includes('connection refused')) {
          fatalError = 'Connection refused. The Botpress API may be unavailable.'
        }

        // If we detected a fatal error, emit it and kill the process
        if (fatalError) {
          const errorObj = {
            exitCode: 1,
            stderr: this.stderrLines.join('\n'),
            message: fatalError,
          }
          this.emit('error', errorObj)

          // Kill the process
          if (this.childProcess) {
            this.childProcess.kill('SIGTERM')
          }
        }

        // Parse stderr as well (some status messages might be there)
        this.parseOutput(text)
        this.emit('stderr', text)
      })
    }

    // Handle process exit
    this.childProcess.then(
      () => {
        // Process exited normally
      },
      (error: any) => {
        if (this.killed) {
          return
        }

        // Build error message with stderr
        let errorMessage = error.message || 'Process exited with error'
        if (this.stderrLines.length > 0) {
          errorMessage += `\n\nLast ${this.stderrLines.length} lines of stderr:\n${this.stderrLines.join('\n')}`
        }

        const errorObj = {
          exitCode: error.exitCode ?? 1,
          stderr: this.stderrLines.join('\n'),
          message: errorMessage,
        }

        this.emit('error', errorObj)
      }
    )
  }

  private parseOutput(text: string): void {
    const now = Date.now()
    const lowerText = text.toLowerCase()

    // Parse status changes and emit progress events
    if (lowerText.includes('generating') && lowerText.includes('typing')) {
      const event: BpDevProgressEvent = {
        type: 'generating',
        startTime: now,
      }
      this.progressEvents.set('generating', event)
      this.emit('progress', event)
    } else if (lowerText.includes('bundling') && lowerText.includes('bot')) {
      // End generating phase
      const generatingEvent = this.progressEvents.get('generating')
      if (generatingEvent && generatingEvent.type === 'generating' && !generatingEvent.endTime) {
        const updatedEvent: BpDevProgressEvent = {
          ...generatingEvent,
          endTime: now,
        }
        this.progressEvents.set('generating', updatedEvent)
        this.emit('progress', updatedEvent)
      }

      // Start bundling phase
      const event: BpDevProgressEvent = {
        type: 'bundling',
        startTime: now,
      }
      this.progressEvents.set('bundling', event)
      this.emit('progress', event)
    } else if (lowerText.includes('deploying') && lowerText.includes('dev')) {
      // End bundling phase
      const bundlingEvent = this.progressEvents.get('bundling')
      if (bundlingEvent && bundlingEvent.type === 'bundling' && !bundlingEvent.endTime) {
        const updatedEvent: BpDevProgressEvent = {
          ...bundlingEvent,
          endTime: now,
        }
        this.progressEvents.set('bundling', updatedEvent)
        this.emit('progress', updatedEvent)
      }

      // Start deploying phase
      const event: BpDevProgressEvent = {
        type: 'deploying',
        startTime: now,
      }
      this.progressEvents.set('deploying', event)
      this.emit('progress', event)
    }

    // Parse build time
    const buildTimeMatch = text.match(/build\s+completed\s+in\s+(\d+)\s*ms/i)
    if (buildTimeMatch && buildTimeMatch[1]) {
      const bundlingEvent = this.progressEvents.get('bundling')
      if (bundlingEvent && bundlingEvent.type === 'bundling') {
        const updatedEvent: BpDevProgressEvent = {
          ...bundlingEvent,
          data: {
            buildTime: parseInt(buildTimeMatch[1], 10),
          },
        }
        this.progressEvents.set('bundling', updatedEvent)
        this.emit('progress', updatedEvent)
      }
    }

    // Parse bot deployment info
    const deployMatch = text.match(
      /dev\s+bot\s+deployed\s+with\s+id\s+["']?([a-f0-9-]+)["']?\s+at\s+["']?(https?:\/\/[^\s"']+)["']?/i
    )
    if (deployMatch && deployMatch[1] && deployMatch[2]) {
      // End deploying phase
      const deployingEvent = this.progressEvents.get('deploying')
      if (deployingEvent && deployingEvent.type === 'deploying' && !deployingEvent.endTime) {
        const updatedEvent: BpDevProgressEvent = {
          ...deployingEvent,
          endTime: now,
          data: {
            botId: deployMatch[1],
            tunnelUrl: deployMatch[2],
          },
        }
        this.progressEvents.set('deploying', updatedEvent)
        this.emit('progress', updatedEvent)
      }
    }

    // Parse listening status
    const listeningMatch = text.match(/listening\s+on\s+port\s+(\d+)/i)
    if (listeningMatch && listeningMatch[1]) {
      const port = parseInt(listeningMatch[1], 10)
      const event: BpDevProgressEvent = {
        type: 'listening',
        startTime: now,
        data: {
          port,
        },
      }
      this.progressEvents.set('listening', event)
      this.emit('progress', event)

      // Optimize source map after dev server is listening
      this.optimizeSourceMapIfExists().catch(() => {
        // Silently ignore source map optimization errors
      })
    }
  }

  private async optimizeSourceMapIfExists(): Promise<void> {
    const { botPath } = this.options
    const sourceMapPath = join(botPath, '.botpress', 'dist', 'index.cjs.map')

    try {
      // Check if source map exists
      await access(sourceMapPath)

      // Get the agent's src directory (parent of botPath)
      const agentSrcPath = join(botPath, '..', '..', 'src')

      // Optimize the source map
      await optimizeSourceMap({
        sourceMapPath,
        agentSrcPath,
      })
    } catch {
      // Source map doesn't exist or optimization failed - not critical
    }
  }

  kill(signal: NodeJS.Signals = 'SIGTERM'): void {
    this.killed = true
    if (this.childProcess) {
      this.childProcess.kill(signal)
    }
  }

  async restart(): Promise<BpDevCommand> {
    this.kill('SIGTERM')
    // Wait a bit for the process to clean up
    await new Promise((resolve) => setTimeout(resolve, 1000))
    // Create new instance and start it
    const newCommand = new BpDevCommand(this.options)
    await newCommand.run()
    return newCommand
  }

  getProgressEvent(type: BpDevProgressEvent['type']): BpDevProgressEvent | undefined {
    return this.progressEvents.get(type)
  }

  getAllProgressEvents(): BpDevProgressEvent[] {
    return Array.from(this.progressEvents.values())
  }
}
